import {
  specialCharactersMessaging,
  BASICINPUTSCHEMA,
  getCurrentProductFromUrl,
  getCurrentProductFilter,
  getFilterTypeFromPathname,
  getLessonsLink,
  sortCurriculumTypesWithKFirst,
  createSchema,
  BaseValidator,
  getInitialSelectedSchool,
  checkBlockingValidations as sharedCheckBlockingValidations,
  checkLimitReached as sharedCheckLimitReached,
  getRelevantCourseList,
} from 'experienceComponents/CreateInstanceShared/dataUtils.js'

import {
  TICourseNameToProgramMap,
  K5_PROGRAM_TYPE,
  MSP_PROGRAM_TYPE,
  GRADE_K2_TIERED_INTERVENTIONS,
  GRADE_35_TIERED_INTERVENTIONS,
  GRADE_68_TIERED_INTERVENTIONS,
} from 'utils/program/types'
import { k8V2ContentfulMappings } from 'utils/product/contentfulMappings'
import { getCourseInstancePath } from 'components/dashboard/ClassCard/helpers.js'

// Grade display mapping for user-friendly labels
const GRADE_DISPLAY_MAP = {
  [GRADE_K2_TIERED_INTERVENTIONS]: 'Grade K-2',
  [GRADE_35_TIERED_INTERVENTIONS]: 'Grade 3-5',
  [GRADE_68_TIERED_INTERVENTIONS]: 'Grade 6-8',
}

const VALID_TIER2_CURRICULUM_TYPES = [
  GRADE_68_TIERED_INTERVENTIONS,
  GRADE_35_TIERED_INTERVENTIONS,
  GRADE_K2_TIERED_INTERVENTIONS,
]

// DO NOT MOVE TO smallGroupConfig.js. Kept here to prevent circular dependency with that file.
export const SMALL_GROUP_CONFIG = {
  schemaName: 'CREATESMALLGROUPSCHEMA',
  requiresSite: false,
  requiresDates: false,
  filterFunction: 'filterSmallGroupCurriculumTypes',
  sortFunction: 'sortCurriculumTypesWithKFirst',
  getRelevantCourseList: courses => getRelevantCourseList(courses, true),
  duplicateMessage: 'You may not have a duplicate small group name',
  validatorClass: 'CreateSmallGroupValidator',
  validationType: 'smallGroup',
}

// Function to convert internal grade values to display values
export const convertGradeToDisplayValue = gradeValue => {
  return GRADE_DISPLAY_MAP[gradeValue] || gradeValue
}

// Filter small group curriculum types
export const filterSmallGroupCurriculumTypes = (grades = []) => {
  return grades.filter(grade => VALID_TIER2_CURRICULUM_TYPES.includes(grade))
}

// Convert display name to curriculum type
export const convertGradeNameToLabel = gradeName => {
  const displayToCurriculumMap = Object.fromEntries(
    Object.entries(GRADE_DISPLAY_MAP).map(([curriculumType, displayLabel]) => [
      displayLabel,
      curriculumType,
    ]),
  )
  return displayToCurriculumMap[gradeName] || gradeName
}

// Get program name from selected grade
export const getProgramName = selectedGrade => {
  return TICourseNameToProgramMap[selectedGrade]
}

const INSTRUCTOR_RESPONSE_KEY_BY_PROGRAM = {
  [K5_PROGRAM_TYPE]: 'K5SelDigital',
  [MSP_PROGRAM_TYPE]: 'MspDigital',
}

const TIER2_COURSE_CODE_BY_CURRICULUM_TYPE = {
  [GRADE_K2_TIERED_INTERVENTIONS]: {
    contentGroupCode: K5_PROGRAM_TYPE,
    courseCode: 'k-2_core_tier-2_siteEducator',
  },
  [GRADE_35_TIERED_INTERVENTIONS]: {
    contentGroupCode: K5_PROGRAM_TYPE,
    courseCode: '3-5_core_tier-2_siteEducator',
  },
  [GRADE_68_TIERED_INTERVENTIONS]: {
    contentGroupCode: MSP_PROGRAM_TYPE,
    courseCode: '6-8_core_tier-2_siteEducator',
  },
}

export const getTier2CourseCodeFromCurriculumType = curriculumType => {
  if (!curriculumType) return null
  return (
    TIER2_COURSE_CODE_BY_CURRICULUM_TYPE[curriculumType]?.courseCode ?? null
  )
}

export const getInstructorResponseKeyForCurriculumType = curriculumType => {
  const contentGroupCode = getProgramName(curriculumType)
  return INSTRUCTOR_RESPONSE_KEY_BY_PROGRAM[contentGroupCode] ?? null
}

// Create view class behavior
export const createViewClassBehavior = (state, props) => {
  const { redirectToMyClasses, pathName, currentSite } = props || {}
  const { selectedCourse, selectedGrade, selectedSchool } = state || {}
  const { id: courseInstanceId } = selectedCourse || {}

  // If we have the newly created instance id, build a direct route to it
  if (courseInstanceId && redirectToMyClasses) {
    try {
      // Determine product slug from URL (e.g., 'elementary' | 'middle-school')
      const currentProduct = getCurrentProductFromUrl(pathName)
      const programSlug = currentProduct?.productName

      // Use the display grade from state (e.g., 'Grade K-2', 'Grade 6-8')
      const displayGrade = selectedGrade

      // Convert display grade to internal label to look up defaultUnit
      const internalGrade = convertGradeNameToLabel(displayGrade)
      const mapping = Array.isArray(k8V2ContentfulMappings)
        ? k8V2ContentfulMappings.find(m => m.courseName === internalGrade)
        : null
      const defaultUnit = mapping?.defaultUnit || ''

      // Preferred: Build the tier-2 course instance path
      if (programSlug && displayGrade) {
        let path = getCourseInstancePath({
          program: programSlug,
          gradeDisplayTitle: displayGrade,
          instance: courseInstanceId,
          tier: 2,
          defaultUnit,
        })

        // Append site param if available
        const siteId = selectedSchool?.siteId || currentSite?.siteId
        if (siteId) {
          path = `${path}&site=${siteId}`
        }
        redirectToMyClasses(path)
      } else if (programSlug) {
        // Fallback: generic teach route still binds the instance via class query
        const siteId = selectedSchool?.siteId || currentSite?.siteId
        let fallbackPath = `/product/${programSlug}/lessons/teach/?class=${courseInstanceId}`
        if (siteId) {
          fallbackPath = `${fallbackPath}&site=${siteId}`
        }
        redirectToMyClasses(fallbackPath)
      } else {
        // Last resort: default
        redirectToMyClasses()
      }
    } catch (e) {
      // On any error, fallback to default redirect
      redirectToMyClasses()
    }
  }

  return { newState: { ...state, modalContent: null } }
}

// Create component-specific schema
export const CREATESMALLGROUPSCHEMA = createSchema(SMALL_GROUP_CONFIG)

// Create component-specific validator class
export class CreateSmallGroupValidator extends BaseValidator {
  constructor(state, props) {
    super(state, props, SMALL_GROUP_CONFIG)
  }
}

// Re-export shared functions for backward compatibility
export {
  specialCharactersMessaging,
  BASICINPUTSCHEMA,
  getCurrentProductFromUrl,
  getCurrentProductFilter,
  getFilterTypeFromPathname,
  getLessonsLink,
  sortCurriculumTypesWithKFirst,
  getInitialSelectedSchool,
}

// Component-specific wrapper functions
export const checkBlockingValidations = async (state, props) => {
  return sharedCheckBlockingValidations(state, props, SMALL_GROUP_CONFIG)
}

export const checkLimitReached = (pathName, lms, maxClasses) => {
  return sharedCheckLimitReached(pathName, lms, maxClasses)
}
